import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { useSchool } from '../../context/SchoolContext';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { jsPDF } from 'jspdf';

const AITestGenerator = () => {
  const navigate = useNavigate();
  const { school } = useSchool();
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';
  const isUnlocked = isSuperAdmin || school?.feature_ai_test_gen !== 0;

  // Mode: 'auto' or 'content'
  const [activeTab, setActiveTab] = useState('auto');
  
  // Language: 'english' or 'urdu'
  const [language, setLanguage] = useState('english');
  const isUrdu = language === 'urdu';

  // Common Form State
  const [subject, setSubject] = useState('Mathematics');
  const [className, setClassName] = useState('Class 10');
  const [topic, setTopic] = useState('Algebra & Equations');
  const [difficulty, setDifficulty] = useState('medium');

  // Question counts & Marks per Question
  const [mcqCount, setMcqCount] = useState(10);
  const [mcqMarks, setMcqMarks] = useState(1);

  const [shortCount, setShortCount] = useState(5);
  const [shortMarks, setShortMarks] = useState(4);

  const [longCount, setLongCount] = useState(2);
  const [longMarks, setLongMarks] = useState(10);

  const [totalMarks, setTotalMarks] = useState(50);

  // Custom Instructions
  const [customInstructions, setCustomInstructions] = useState('');

  // Tab 2 - Content State
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [contentText, setContentText] = useState('');

  // Generation & Result State
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatedTest, setGeneratedTest] = useState(null);
  const [showAnswerKey, setShowAnswerKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Quota & Queue States
  const [quotaInfo, setQuotaInfo] = useState({ remaining: 10, limit: 10, currentCount: 0 });
  const [queueStatus, setQueueStatus] = useState({ active: false, position: 0, estimatedSec: 0, status: '' });

  // Preset Subjects & Classes
  const subjectList = [
    'Mathematics',
    'Physics',
    'Chemistry',
    'Biology',
    'English',
    'Urdu',
    'Computer Science',
    'Islamic Studies',
    'Pakistan Studies',
    'General Science'
  ];

  const classList = [
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
    'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
    'Class 11 (FSc/FA)', 'Class 12 (FSc/FA)'
  ];

  // Quick instruction presets
  const instructionPresets = isUrdu ? [
    'تعریفات اور بنیادی تصورات پر توجہ دیں',
    'عددی مسائل (Numerical Problems) شامل کریں',
    'صرف اہم تصوراتی سوالات بنائیں',
    'امتحانی فارمیٹ کے مطابق مشکل سوالات بنائیں'
  ] : [
    'Focus on definitions and core concepts',
    'Include numerical & calculation problems',
    'Focus on conceptual reasoning questions',
    'Strict examination standard layout'
  ];

  // Fetch school daily quota usage
  const fetchQuotaStatus = async () => {
    try {
      const res = await api.get('/api/v1/ai-tests/quota-status');
      if (res.data?.success && res.data.data) {
        setQuotaInfo(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch quota status:', err);
    }
  };

  useEffect(() => {
    fetchQuotaStatus();
  }, []);

  if (!isUnlocked) {
    return (
      <DashboardLayout>
        <div className="bg-slate-900 text-white rounded-3xl p-8 shadow-xl text-center space-y-4 max-w-2xl mx-auto my-12 border border-purple-500/30">
          <div className="w-16 h-16 rounded-3xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-3xl font-extrabold mx-auto border border-amber-500/30">
            🔒
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight">AI Test Generator - Locked Pro Feature</h2>
          <p className="text-sm text-slate-300">
            The Automated AI Test & Exam Paper Generator module is not active for <strong>{school?.name || 'your school portal'}</strong>.
          </p>
          <div className="pt-4">
            <a
              href="mailto:support@waleedtech.com?subject=Upgrade to Unlock AI Test Generator"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition"
            >
              <span>✨</span>
              <span>Upgrade to Enterprise AI Tier</span>
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Auto calculate total marks based on counts and custom marks per question
  const calculateTotalMarks = (mCnt, mMrk, sCnt, sMrk, lCnt, lMrk) => {
    return (mCnt * mMrk) + (sCnt * sMrk) + (lCnt * lMrk);
  };

  const handleParamChange = (field, val) => {
    const num = Math.max(0, parseInt(val, 10) || 0);
    let mC = mcqCount, mM = mcqMarks;
    let sC = shortCount, sM = shortMarks;
    let lC = longCount, lM = longMarks;

    if (field === 'mcqCount') { mC = num; setMcqCount(num); }
    if (field === 'mcqMarks') { mM = num; setMcqMarks(num); }
    if (field === 'shortCount') { sC = num; setShortCount(num); }
    if (field === 'shortMarks') { sM = num; setShortMarks(num); }
    if (field === 'longCount') { lC = num; setLongCount(num); }
    if (field === 'longMarks') { lM = num; setLongMarks(num); }

    setTotalMarks(calculateTotalMarks(mC, mM, sC, sM, lC, lM));
  };

  // Handle File Drag & Drop / File Upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Start polling queue status for active job
  const startQueuePolling = (jobId) => {
    setQueueStatus({ active: true, position: 1, estimatedSec: 2, status: 'queued' });

    const timer = setInterval(async () => {
      try {
        const res = await api.get(`/api/v1/ai-tests/queue-status/${jobId}`);
        if (res.data?.success && res.data.data) {
          const info = res.data.data;
          if (info.status === 'completed') {
            clearInterval(timer);
            setQueueStatus({ active: false, position: 0, estimatedSec: 0, status: 'completed' });
            setGeneratedTest(info.result);
            setGenerating(false);
            setFeedbackMsg({
              type: 'success',
              text: isUrdu ? 'ٹیسٹ کامیابی سے تیار ہو گیا ہے!' : 'Test paper generated successfully!'
            });
            fetchQuotaStatus();
          } else if (info.status === 'failed') {
            clearInterval(timer);
            setQueueStatus({ active: false, position: 0, estimatedSec: 0, status: 'failed' });
            setGenerating(false);
            setFeedbackMsg({
              type: 'error',
              text: info.error || (isUrdu ? 'ٹیسٹ تیار کرنے میں ناکامی ہوئی۔' : 'Failed to generate test paper.')
            });
          } else {
            setQueueStatus({
              active: true,
              position: info.position || 1,
              estimatedSec: info.estimatedSec || 2,
              status: info.status
            });
          }
        }
      } catch (err) {
        console.error('Error polling queue status:', err);
      }
    }, 1500);
  };

  // Handle AI Test Generation Call
  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setFeedbackMsg({ type: '', text: '' });
      setGeneratedTest(null);

      if (activeTab === 'auto') {
        const payload = {
          subject,
          class_name: className,
          topic,
          difficulty,
          mcq_count: mcqCount,
          short_count: shortCount,
          long_count: longCount,
          mcq_marks: mcqMarks,
          short_marks: shortMarks,
          long_marks: longMarks,
          total_marks: totalMarks,
          language,
          custom_instructions: customInstructions
        };

        const res = await api.post('/api/v1/ai-tests/generate', payload, { timeout: 120000 });
        if (res.status === 202 && res.data?.data?.jobId) {
          startQueuePolling(res.data.data.jobId);
          fetchQuotaStatus();
        } else if (res.data?.success) {
          setGeneratedTest(res.data.data);
          setGenerating(false);
          setFeedbackMsg({ type: 'success', text: isUrdu ? 'ٹیسٹ کامیابی سے تیار ہو گیا ہے!' : 'Test paper generated successfully!' });
          fetchQuotaStatus();
        } else {
          setGenerating(false);
          setFeedbackMsg({ type: 'error', text: res.data?.message || 'Failed to generate test paper.' });
        }
      } else {
        // Content-based mode
        if (!contentText.trim() && uploadedFiles.length === 0) {
          setFeedbackMsg({
            type: 'error',
            text: isUrdu ? 'براہ کرم مواد کا متن پیسٹ کریں۔' : 'Please paste your reference text into the box.'
          });
          setGenerating(false);
          return;
        }

        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('class_name', className);
        formData.append('difficulty', difficulty);
        formData.append('mcq_count', mcqCount);
        formData.append('short_count', shortCount);
        formData.append('long_count', longCount);
        formData.append('mcq_marks', mcqMarks);
        formData.append('short_marks', shortMarks);
        formData.append('long_marks', longMarks);
        formData.append('total_marks', totalMarks);
        formData.append('language', language);
        formData.append('custom_instructions', customInstructions);
        formData.append('content_text', contentText);

        uploadedFiles.forEach((file) => {
          formData.append('files', file);
        });

        const res = await api.post('/api/v1/ai-tests/generate-from-content', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });

        if (res.status === 202 && res.data?.data?.jobId) {
          startQueuePolling(res.data.data.jobId);
          fetchQuotaStatus();
        } else if (res.data?.success) {
          setGeneratedTest(res.data.data);
          setGenerating(false);
          setFeedbackMsg({ type: 'success', text: isUrdu ? 'مواد پر مبنی ٹیسٹ تیار ہو گیا ہے!' : 'Content-based test generated successfully!' });
          fetchQuotaStatus();
        } else {
          setGenerating(false);
          setFeedbackMsg({ type: 'error', text: res.data?.message || 'Failed to generate test from content.' });
        }
      }
    } catch (error) {
      console.error('Error generating AI test:', error);
      setGenerating(false);
      setFeedbackMsg({
        type: 'error',
        text: error.response?.data?.message || error.message || 'An error occurred during AI generation.'
      });
    }
  };

  // Save Test to DB
  const handleSaveTest = async () => {
    if (!generatedTest) return;

    try {
      setSaving(true);
      const payload = {
        title: generatedTest.title || `${subject} Test - ${topic || 'Custom'}`,
        subject,
        class_name: className,
        topic: topic || 'Custom Content',
        difficulty,
        total_marks: totalMarks,
        language,
        test_content: generatedTest,
        mode: activeTab
      };

      const res = await api.post('/api/v1/ai-tests/save', payload);
      if (res.data?.success) {
        setFeedbackMsg({
          type: 'success',
          text: isUrdu ? 'ٹیسٹ ڈیٹا بیس میں محفوظ ہو گیا ہے!' : 'Test saved to database successfully!'
        });
      }
    } catch (error) {
      console.error('Error saving test:', error);
      setFeedbackMsg({ type: 'error', text: 'Failed to save test paper.' });
    } finally {
      setSaving(false);
    }
  };

  // Inline edit helper
  const updateMcqQuestion = (index, field, val, optIdx = null) => {
    if (!generatedTest) return;
    const updated = { ...generatedTest };
    const mcqs = updated.sections?.mcqs || updated.mcqs;
    if (!mcqs || !mcqs[index]) return;

    if (optIdx !== null) {
      if (Array.isArray(mcqs[index].options)) {
        mcqs[index].options[optIdx] = val;
      } else if (mcqs[index].options && typeof mcqs[index].options === 'object') {
        const keys = Object.keys(mcqs[index].options);
        const targetKey = keys[optIdx] !== undefined ? keys[optIdx] : optIdx;
        mcqs[index].options[targetKey] = val;
      }
    } else {
      mcqs[index][field] = val;
    }
    setGeneratedTest(updated);
  };

  const updateQuestionItem = (sectionKey, index, field, val) => {
    if (!generatedTest) return;
    const updated = { ...generatedTest };
    const items = updated.sections?.[sectionKey] || updated[sectionKey];
    if (items && items[index]) {
      items[index][field] = val;
    }
    
    // Recalculate preview total marks if question mark was changed
    let sum = 0;
    const mcqs = updated.sections?.mcqs || updated.mcqs || [];
    const short = updated.sections?.short || updated.short || [];
    const long = updated.sections?.long || updated.long || [];

    mcqs.forEach((q) => sum += Number(q.marks || 1));
    short.forEach((q) => sum += Number(q.marks || 4));
    long.forEach((q) => sum += Number(q.marks || 10));
    setTotalMarks(sum);

    setGeneratedTest(updated);
  };

  // Print Test using native browser window.print()
  const handlePrint = () => {
    window.print();
  };

  // Download PDF using jsPDF for Test Paper
  const handleDownloadPDF = () => {
    if (!generatedTest) return;
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(school?.name || 'School Test Paper', 105, 15, { align: 'center' });
      doc.setFontSize(12);
      doc.text(generatedTest.title || 'Test Paper', 105, 23, { align: 'center' });
      doc.text(`Subject: ${subject} | Class: ${className} | Total Marks: ${totalMarks}`, 105, 30, { align: 'center' });
      
      let y = 42;
      doc.setFontSize(10);

      if (generatedTest.instructions && generatedTest.instructions.length > 0) {
        doc.text('Instructions: ' + generatedTest.instructions.join(' '), 14, y);
        y += 10;
      }

      const mcqs = generatedTest.sections?.mcqs || generatedTest.mcqs || [];
      if (mcqs.length > 0) {
        doc.setFontSize(12);
        doc.text('Section A: Multiple Choice Questions', 14, y);
        y += 8;
        doc.setFontSize(10);
        mcqs.forEach((q, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`Q${idx + 1}. ${q.question} [${q.marks || 1} mark]`, 14, y);
          y += 6;
          if (q.options) {
            let optsArr = Array.isArray(q.options) ? q.options : Object.values(q.options);
            doc.text(`   ${optsArr.join('   ')}`, 14, y);
            y += 8;
          }
        });
      }

      const shorts = generatedTest.sections?.short || generatedTest.short || [];
      if (shorts.length > 0) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text('Section B: Short Questions', 14, y);
        y += 8;
        doc.setFontSize(10);
        shorts.forEach((q, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`Q${idx + 1}. ${q.question} [${q.marks || 4} marks]`, 14, y);
          y += 10;
        });
      }

      const longs = generatedTest.sections?.long || generatedTest.long || [];
      if (longs.length > 0) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text('Section C: Long / Essay Questions', 14, y);
        y += 8;
        doc.setFontSize(10);
        longs.forEach((q, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`Q${idx + 1}. ${q.question} [${q.marks || 10} marks]`, 14, y);
          y += 12;
        });
      }

      doc.save(`${subject}_${className}_Test.pdf`);
    } catch (err) {
      console.error('jsPDF export error:', err);
      window.print();
    }
  };

  // Download PDF for Answer Key & Rubrics
  const handleDownloadAnswerKeyPDF = () => {
    if (!generatedTest) return;
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(school?.name || 'School Test Paper', 105, 15, { align: 'center' });
      doc.setFontSize(13);
      doc.text(`${generatedTest.title || subject} - ANSWER KEY & RUBRICS`, 105, 23, { align: 'center' });
      doc.setFontSize(10);
      doc.text(`Subject: ${subject} | Class: ${className} | Date: ${new Date().toLocaleDateString()}`, 105, 30, { align: 'center' });

      let y = 42;

      // Section A: MCQs Answer Key
      const mcqKey = generatedTest.answer_key?.mcqs || (Array.isArray(generatedTest.answer_key) ? generatedTest.answer_key : []);
      if (mcqKey.length > 0) {
        doc.setFontSize(12);
        doc.text('Section A: MCQ Answers & Solutions', 14, y);
        y += 8;
        doc.setFontSize(10);
        mcqKey.forEach((item, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          const ansStr = typeof item === 'object'
            ? `Q${item.q || idx + 1}: [Ans: ${item.answer || 'A'}] ${item.text || ''} - ${item.reason || ''}`
            : `Q${idx + 1}: ${item}`;
          doc.text(ansStr, 14, y);
          y += 7;
        });
        y += 5;
      }

      // Section B: Short Rubric
      const shortKey = generatedTest.answer_key?.short || [];
      if (shortKey.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text('Section B: Short Questions Marking Rubric', 14, y);
        y += 8;
        doc.setFontSize(10);
        shortKey.forEach((item, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          const rubStr = `Q${item.q || idx + 1} (${item.marks || 4} Marks): ${item.rubric || 'Full credit for core definition and example.'}`;
          doc.text(rubStr, 14, y);
          y += 8;
        });
        y += 5;
      }

      // Section C: Long Rubric
      const longKey = generatedTest.answer_key?.long || [];
      if (longKey.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text('Section C: Long Questions Marking Rubric', 14, y);
        y += 8;
        doc.setFontSize(10);
        longKey.forEach((item, idx) => {
          if (y > 270) { doc.addPage(); y = 20; }
          const rubStr = `Q${item.q || idx + 1} (${item.marks || 10} Marks): ${item.rubric || 'Detailed conceptual breakdown.'}`;
          doc.text(rubStr, 14, y);
          y += 9;
        });
      }

      doc.save(`${subject}_${className}_AnswerKey.pdf`);
    } catch (err) {
      console.error('jsPDF Answer Key export error:', err);
    }
  };

  return (
    <div className={`min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 font-sans ${isUrdu ? 'rtl' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif, sans-serif", direction: 'rtl' } : {}}>
      
      {/* Printable Area Styles */}
      <style>{`
        @media print {
          nav, header, .navbar { display: none !important; }
          .test-paper { margin: 0; padding: 20px; }
          body {
            background-color: white !important;
            color: black !important;
            font-family: ${isUrdu ? "'Noto Nastaliq Urdu', serif" : "sans-serif"} !important;
            direction: ${isUrdu ? "rtl" : "ltr"} !important;
          }
          .no-print, button {
            display: none !important;
          }
          .print-container {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            background-color: white !important;
            color: black !important;
          }
          .page-break {
            page-break-before: always;
          }
        }
      `}</style>

      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Top Bar Header & Controls */}
        <div className="no-print bg-slate-800 border border-slate-700 rounded-xl p-5 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <span className="text-2xl">⚡</span>
              <h1 className="text-2xl font-bold text-white tracking-wide">
                {isUrdu ? 'اے آئی ٹیسٹ اور امتحان جنریٹر' : 'AI Test & Exam Generator'}
              </h1>
            </div>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">
              {isUrdu
                ? 'Groq AI کے ذریعے چند سیکنڈوں میں معیاری ٹیسٹ پیپر خودکار یا مواد کی بنیاد پر تیار کریں۔'
                : 'Instantly generate school test papers using Groq AI with full customization and dual mode support.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Daily Quota Badge */}
            <div className="bg-slate-900 border border-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-2 rtl:space-x-reverse">
              <span className="text-amber-400">📊 Quota:</span>
              <span className="text-emerald-400 font-bold">{quotaInfo.remaining} / {quotaInfo.limit} left today</span>
            </div>

            {/* Saved Tests Link */}
            <button
              onClick={() => navigate('/ai-tests/saved')}
              className="bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium text-xs sm:text-sm px-3.5 py-2 rounded-lg border border-slate-600 transition flex items-center space-x-1.5 rtl:space-x-reverse"
            >
              <span>📁</span>
              <span>{isUrdu ? 'محفوظ شدہ ٹیسٹ' : 'Saved Tests'}</span>
            </button>

            {/* Language Toggle Switch */}
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-1 flex items-center space-x-1 rtl:space-x-reverse">
              <button
                onClick={() => setLanguage('english')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition ${language === 'english' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('urdu')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition ${language === 'urdu' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
              >
                اردو
              </button>
            </div>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedbackMsg.text && (
          <div className={`no-print p-4 rounded-lg text-sm border flex items-center justify-between ${
            feedbackMsg.type === 'success' ? 'bg-emerald-950/80 border-emerald-700 text-emerald-300' : 'bg-rose-950/80 border-rose-700 text-rose-300'
          }`}>
            <span>{feedbackMsg.text}</span>
            <button onClick={() => setFeedbackMsg({ type: '', text: '' })} className="font-bold text-lg leading-none">&times;</button>
          </div>
        )}

        {/* Queue Progress Banner */}
        {queueStatus.active && (
          <div className="no-print bg-indigo-950 border border-indigo-700 p-4 rounded-xl text-indigo-200 flex items-center justify-between animate-pulse">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <span className="text-2xl animate-spin">⏳</span>
              <div>
                <div className="font-bold text-sm">
                  {isUrdu ? 'ٹیسٹ پائپ لائن میں عمل میں لایا جا رہا ہے...' : 'AI Test Generation in Queue'}
                </div>
                <div className="text-xs text-indigo-300">
                  {isUrdu
                    ? `صف میں نمبر: #${queueStatus.position} | تخمینہ وقت: ${queueStatus.estimatedSec} سیکنڈ`
                    : `Queue Position: #${queueStatus.position} | Estimated wait time: ${queueStatus.estimatedSec} seconds`}
                </div>
              </div>
            </div>
            <div className="text-xs bg-indigo-900 border border-indigo-600 px-3 py-1 rounded-full font-mono">
              Status: {queueStatus.status}
            </div>
          </div>
        )}

        {/* Main Generator Box */}
        <div className="no-print bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden">
          
          {/* Tabs Navigation */}
          <div className="flex border-b border-slate-700 bg-slate-900/60">
            <button
              onClick={() => setActiveTab('auto')}
              className={`flex-1 py-3.5 px-4 font-semibold text-sm sm:text-base transition flex items-center justify-center space-x-2 rtl:space-x-reverse border-b-2 ${
                activeTab === 'auto'
                  ? 'border-blue-500 text-blue-400 bg-slate-800/80'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
              }`}
            >
              <span>🤖</span>
              <span>{isUrdu ? 'خودکار ٹیسٹ جنریٹر (Auto)' : 'Auto Generate'}</span>
            </button>

            <button
              onClick={() => setActiveTab('content')}
              className={`flex-1 py-3.5 px-4 font-semibold text-sm sm:text-base transition flex items-center justify-center space-x-2 rtl:space-x-reverse border-b-2 ${
                activeTab === 'content'
                  ? 'border-blue-500 text-blue-400 bg-slate-800/80'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
              }`}
            >
              <span>📄</span>
              <span>{isUrdu ? 'متن سے ٹیسٹ بنائیں (From Text Content)' : 'From Text Content'}</span>
            </button>
          </div>

          <div className="p-5 sm:p-7 space-y-6">
            
            {/* TAB 1: Auto Generate Form */}
            {activeTab === 'auto' && (
              <div className="space-y-6">
                
                {/* Step 1 - Settings */}
                <div className="space-y-3">
                  <h3 className="text-sm uppercase tracking-wider font-bold text-blue-400">
                    {isUrdu ? 'مرحلہ 1: ٹیسٹ کی بنیادی ترتیبات' : 'Step 1: Test Parameters'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">
                        {isUrdu ? 'مضمون (Subject)' : 'Subject'}
                      </label>
                      <select
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {subjectList.map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">
                        {isUrdu ? 'کلاس (Class)' : 'Class'}
                      </label>
                      <select
                        value={className}
                        onChange={(e) => setClassName(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {classList.map((cls) => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">
                        {isUrdu ? 'عنوان / ٹاپک (Topic)' : 'Topic Name'}
                      </label>
                      <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder={isUrdu ? 'مثلاً: الجبرا، نیوٹن کے قوانین...' : 'e.g. Quadratic Equations'}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">
                        {isUrdu ? 'مشکل کی سطح (Difficulty)' : 'Difficulty Level'}
                      </label>
                      <div className="grid grid-cols-3 gap-1 bg-slate-900 p-1 rounded-lg border border-slate-700 text-xs">
                        {['easy', 'medium', 'hard'].map((diff) => (
                          <button
                            key={diff}
                            type="button"
                            onClick={() => setDifficulty(diff)}
                            className={`py-1.5 rounded text-center capitalize transition font-medium ${
                              difficulty === diff ? 'bg-blue-600 text-white font-bold' : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            {diff}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2 - Question Counts & Custom Marks Assignment */}
                <div className="space-y-3 pt-3 border-t border-slate-700/60">
                  <h3 className="text-sm uppercase tracking-wider font-bold text-blue-400">
                    {isUrdu ? 'مرحلہ 2: سوالات کی تعداد اور فی سوال نمبرات (Custom Marks)' : 'Step 2: Flexible Question Quantity & Custom Marks'}
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 bg-slate-900/60 p-4 rounded-xl border border-slate-700/70">
                    
                    {/* MCQ Count & Marks */}
                    <div className="space-y-2 border-r border-slate-700/50 pr-3 rtl:border-r-0 rtl:border-l rtl:pl-3">
                      <label className="block text-xs font-semibold text-slate-200">
                        {isUrdu ? 'ایم سی کیوز (MCQs)' : 'MCQ Questions'}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'تعداد' : 'Qty'}</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={mcqCount}
                            onChange={(e) => handleParamChange('mcqCount', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'فی نمبر' : 'Marks'}</span>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={mcqMarks}
                            onChange={(e) => handleParamChange('mcqMarks', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-bold"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Short Count & Marks */}
                    <div className="space-y-2 border-r border-slate-700/50 pr-3 rtl:border-r-0 rtl:border-l rtl:pl-3">
                      <label className="block text-xs font-semibold text-slate-200">
                        {isUrdu ? 'مختصر سوالات (Short)' : 'Short Questions'}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'تعداد' : 'Qty'}</span>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={shortCount}
                            onChange={(e) => handleParamChange('shortCount', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'فی نمبر' : 'Marks'}</span>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={shortMarks}
                            onChange={(e) => handleParamChange('shortMarks', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-bold"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Long Count & Marks */}
                    <div className="space-y-2 border-r border-slate-700/50 pr-3 rtl:border-r-0 rtl:border-l rtl:pl-3">
                      <label className="block text-xs font-semibold text-slate-200">
                        {isUrdu ? 'تفصیلی سوالات (Long)' : 'Long Questions'}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'تعداد' : 'Qty'}</span>
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={longCount}
                            onChange={(e) => handleParamChange('longCount', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400">{isUrdu ? 'فی نمبر' : 'Marks'}</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={longMarks}
                            onChange={(e) => handleParamChange('longMarks', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-bold"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Auto Total Marks */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-amber-400">
                        {isUrdu ? 'کل نمبر (Total)' : 'Total Calculated Marks'}
                      </label>
                      <input
                        type="number"
                        value={totalMarks}
                        onChange={(e) => setTotalMarks(parseInt(e.target.value, 10) || 0)}
                        className="w-full bg-slate-900 border border-amber-500/50 rounded-lg px-3 py-2 text-base font-extrabold text-amber-300 text-center"
                      />
                    </div>

                  </div>
                </div>

                {/* Step 3 - Custom Instructions */}
                <div className="space-y-3 pt-3 border-t border-slate-700/60">
                  <h3 className="text-sm uppercase tracking-wider font-bold text-blue-400">
                    {isUrdu ? 'مرحلہ 3: استاد کی مخصوص ہدایات' : 'Step 3: Teacher Special Instructions'}
                  </h3>
                  <textarea
                    rows="3"
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder={isUrdu ? 'اے آئی کے لیے خصوصی ہدایات مثلاً: تعریفات پر زیادہ زور دیں، بورڈ کے پیٹرن کے مطابق بنائیں...' : 'Enter any special instructions for AI generation...'}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-blue-500"
                  ></textarea>

                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-slate-400 py-1">{isUrdu ? 'تجویز کردہ ہدایات:' : 'Quick Presets:'}</span>
                    {instructionPresets.map((preset, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setCustomInstructions((prev) => (prev ? `${prev}. ${preset}` : preset))}
                        className="bg-slate-700 hover:bg-slate-600 text-xs text-slate-300 px-2.5 py-1 rounded-full border border-slate-600 transition"
                      >
                        + {preset}
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            )}

            {/* TAB 2: From Text Content Form */}
            {activeTab === 'content' && (
              <div className="space-y-6">
                
                {/* Step 1 - Paste Text Content */}
                <div className="space-y-3">
                  <h3 className="text-sm uppercase tracking-wider font-bold text-blue-400">
                    {isUrdu ? 'مرحلہ 1: اپنا کتابی یا نوٹس کا متن یہاں پیسٹ کریں' : 'Step 1: Paste Study Content / Textbook Material'}
                  </h3>

                  {/* Direct Text Paste Area */}
                  <div>
                    <textarea
                      rows="6"
                      value={contentText}
                      onChange={(e) => setContentText(e.target.value)}
                      placeholder={isUrdu ? 'یہاں کتاب کا باب، واٹس ایپ نوٹس یا متن کا پیراگراف پیسٹ کریں (مثلاً: لائنز اور اینگلز کی تعریف...)' : 'Paste textbook chapter, lecture notes, or WhatsApp study paragraphs here (e.g. "Lines and angles are formed when parallel lines are cut by a transversal...")'}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-sm text-white focus:ring-2 focus:ring-blue-500 font-mono leading-relaxed"
                    ></textarea>
                  </div>
                </div>

                {/* Step 2 - Question Quantity Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-3 border-t border-slate-700/60">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">{isUrdu ? 'مضمون' : 'Subject'}</label>
                    <select value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
                      {subjectList.map((sub) => (<option key={sub} value={sub}>{sub}</option>))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">{isUrdu ? 'کلاس' : 'Class'}</label>
                    <select value={className} onChange={(e) => setClassName(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
                      {classList.map((cls) => (<option key={cls} value={cls}>{cls}</option>))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">{isUrdu ? 'ایم سی کیوز تعداد' : 'MCQs Quantity'}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={mcqCount}
                      onChange={(e) => handleParamChange('mcqCount', e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-bold text-emerald-400"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">{isUrdu ? 'مختصر سوالات تعداد' : 'Short Questions'}</label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={shortCount}
                      onChange={(e) => handleParamChange('shortCount', e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-bold text-emerald-400"
                    />
                  </div>
                </div>

              </div>
            )}

            {/* Generate Action Button */}
            <div className="pt-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={generating || queueStatus.active}
                style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
                className="w-full sm:w-auto text-white font-bold text-sm sm:text-base px-8 py-3.5 rounded-xl shadow-lg transition flex items-center justify-center space-x-2 rtl:space-x-reverse disabled:opacity-50 cursor-pointer hover:brightness-110"
              >
                {generating ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{isUrdu ? 'ٹیسٹ عمل کی صف میں ہے...' : 'Queueing AI Test Request...'}</span>
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    <span>{isUrdu ? 'ٹیسٹ پیپر تیار کریں (Generate Test)' : 'Generate Test Paper'}</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>

        {/* RESULT SECTION - PREVIEW & ACTIONS */}
        {generatedTest && (
          <div className="space-y-6">
            
            {/* Result Action Bar */}
            <div className="no-print bg-slate-800 border border-slate-700 rounded-xl p-4 shadow-lg flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm font-semibold text-emerald-400">
                <span>✅</span>
                <span>{isUrdu ? 'ٹیسٹ پیش منظر (Preview Card)' : 'Generated Test Preview'}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                >
                  {isEditing ? (isUrdu ? 'ایڈیٹنگ مکمل' : 'Done Editing') : (isUrdu ? 'سوالات اور نمبر ایڈٹ کریں' : 'Edit Questions & Marks')}
                </button>

                <button
                  onClick={handleSaveTest}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition disabled:opacity-50"
                >
                  {saving ? (isUrdu ? 'محفوظ ہو رہا ہے...' : 'Saving...') : (isUrdu ? 'ٹیسٹ محفوظ کریں' : 'Save Test')}
                </button>

                <button
                  onClick={handlePrint}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                >
                  {isUrdu ? 'پرنٹ کریں' : 'Print Test'}
                </button>

                <button
                  onClick={handleDownloadPDF}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                >
                  {isUrdu ? 'PDF پیپر ڈاؤن لوڈ' : 'Download Test PDF'}
                </button>

                <button
                  onClick={handleDownloadAnswerKeyPDF}
                  className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition flex items-center space-x-1 rtl:space-x-reverse"
                >
                  <span>🔑</span>
                  <span>{isUrdu ? 'جوابات کی کلید (PDF)' : 'Download Answer Key PDF'}</span>
                </button>

                <button
                  onClick={() => { setGeneratedTest(null); }}
                  className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold px-3 py-2 rounded-lg transition"
                >
                  {isUrdu ? 'نیا ٹیسٹ بنائیں' : 'Generate New'}
                </button>
              </div>
            </div>

            {/* Printable Test Card Container */}
            <div className="test-paper print-container bg-white text-slate-900 p-6 sm:p-10 rounded-xl shadow-2xl border border-slate-200 space-y-6">
              
              {/* Header Info */}
              <div className="border-b-2 border-slate-900 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  {school?.logo ? (
                    <img src={school.logo} alt="School Logo" className="h-16 w-auto object-contain" />
                  ) : (
                    <div className="text-xl font-black text-blue-900">{school?.name || 'School'}</div>
                  )}
                  <div className="text-right text-xs space-y-1">
                    <div><strong>Date:</strong> ____________________</div>
                    <div><strong>Roll No:</strong> __________________</div>
                  </div>
                </div>

                <div className="text-center space-y-1">
                  <h2 className="text-2xl font-extrabold uppercase tracking-wide text-slate-900">
                    {school?.name || 'SCHOOL PORTAL'}
                  </h2>
                  <h3 className="text-lg font-bold text-slate-800">
                    {generatedTest.title || `${subject} Examination`}
                  </h3>
                  <div className="flex justify-center flex-wrap gap-4 text-xs font-semibold text-slate-700 pt-1">
                    <span><strong>Subject:</strong> {subject}</span>
                    <span>|</span>
                    <span><strong>Class:</strong> {className}</span>
                    <span>|</span>
                    <span><strong>Total Marks:</strong> {totalMarks}</span>
                    <span>|</span>
                    <span><strong>Time Allowed:</strong> 1 Hour 30 Mins</span>
                  </div>
                </div>
              </div>

              {/* Student Name Box */}
              <div className="grid grid-cols-2 gap-4 text-xs font-semibold bg-slate-50 p-2.5 rounded border border-slate-300">
                <div>Student Name: _____________________________________</div>
                <div className="text-right rtl:text-left">Section / Branch: ___________________</div>
              </div>

              {/* Instructions */}
              {generatedTest.instructions && generatedTest.instructions.length > 0 && (
                <div className="text-xs space-y-1 bg-amber-50 p-3 rounded border border-amber-200">
                  <span className="font-bold text-amber-900">{isUrdu ? 'عمومی ہدایات:' : 'General Instructions:'}</span>
                  <ul className="list-disc list-inside text-amber-800">
                    {generatedTest.instructions.map((inst, i) => (
                      <li key={i}>{inst}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* SECTION A: MCQs */}
              {((generatedTest.sections?.mcqs && generatedTest.sections.mcqs.length > 0) || (generatedTest.mcqs && generatedTest.mcqs.length > 0)) && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center border-b pb-2">
                    <h4 className="font-extrabold text-base uppercase text-slate-900">
                      {isUrdu ? 'حصہ اول: کثیر الانتخابی سوالات (MCQs)' : 'Section A: Multiple Choice Questions'}
                    </h4>
                    <span className="text-xs font-bold bg-slate-200 px-2.5 py-1 rounded">
                      [{ (generatedTest.sections?.mcqs || generatedTest.mcqs || []).reduce((acc, curr) => acc + (Number(curr.marks) || 1), 0) } Marks]
                    </span>
                  </div>

                  <div className="space-y-6 text-sm">
                    {(generatedTest.sections?.mcqs || generatedTest.mcqs || []).map((q, idx) => {
                      let rawOptions = [];
                      if (Array.isArray(q.options)) {
                        rawOptions = q.options.map((opt, i) => ({ key: String.fromCharCode(97 + i), text: opt }));
                      } else if (q.options && typeof q.options === 'object') {
                        rawOptions = Object.entries(q.options).map(([k, v]) => ({ key: k, text: v }));
                      }

                      const urduLabels = ['الف', 'ب', 'ج', 'د'];
                      const engLabels = ['A', 'B', 'C', 'D'];

                      return (
                        <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 shadow-sm space-y-3 my-4">
                          <div className="flex justify-between items-start font-bold text-slate-900">
                            <div className="flex-1">
                              <span>Q{idx + 1}. </span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={q.question}
                                  onChange={(e) => updateMcqQuestion(idx, 'question', e.target.value)}
                                  className="w-full border border-blue-400 p-1 text-sm rounded mt-1"
                                />
                              ) : (
                                <span>{q.question}</span>
                              )}
                            </div>
                            {isEditing && (
                              <div className="ml-2 flex items-center space-x-1">
                                <span className="text-xs font-normal text-slate-500">Marks:</span>
                                <input
                                  type="number"
                                  value={q.marks || 1}
                                  onChange={(e) => updateMcqQuestion(idx, 'marks', Number(e.target.value))}
                                  className="w-12 border border-emerald-500 px-1 py-0.5 text-xs text-center font-bold"
                                />
                              </div>
                            )}
                          </div>

                          {/* Options grid with empty answer box: □ الف) option text */}
                          {rawOptions.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs font-medium pt-1">
                              {rawOptions.map((optObj, optIdx) => {
                                const label = isUrdu
                                  ? (urduLabels[optIdx] || 'الف')
                                  : (engLabels[optIdx] || 'A');

                                let cleanText = String(optObj.text || '').replace(/^(?:[□\s]*)(?:[a-dA-Dالفبجد][\).\:-]\s*)?/, '').trim();

                                return (
                                  <div key={optIdx} className="bg-white p-2.5 rounded-lg border border-slate-300 flex items-center space-x-2 rtl:space-x-reverse shadow-xs">
                                    <span className="font-bold text-slate-800 whitespace-nowrap">
                                      □ {label})
                                    </span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={cleanText}
                                        onChange={(e) => updateMcqQuestion(idx, 'options', e.target.value, optIdx)}
                                        className="w-full border border-blue-400 text-xs p-1 rounded"
                                      />
                                    ) : (
                                      <span className="text-slate-800 font-medium">{cleanText}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SECTION B: SHORT QUESTIONS */}
              {((generatedTest.sections?.short && generatedTest.sections.short.length > 0) || (generatedTest.short && generatedTest.short.length > 0)) && (
                <div className="space-y-4 pt-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <h4 className="font-extrabold text-base uppercase text-slate-900">
                      {isUrdu ? 'حصہ دوم: مختصر سوالات' : 'Section B: Short Questions'}
                    </h4>
                    <span className="text-xs font-bold bg-slate-200 px-2.5 py-1 rounded">
                      [{ (generatedTest.sections?.short || generatedTest.short || []).reduce((acc, curr) => acc + (Number(curr.marks) || 4), 0) } Marks]
                    </span>
                  </div>

                  <div className="space-y-4 text-sm">
                    {(generatedTest.sections?.short || generatedTest.short || []).map((q, idx) => (
                      <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 shadow-sm flex justify-between items-start space-x-4 rtl:space-x-reverse my-4">
                        <div className="font-semibold text-slate-900 flex-1">
                          <span>Q{idx + 1}. </span>
                          {isEditing ? (
                            <input
                              type="text"
                              value={q.question}
                              onChange={(e) => updateQuestionItem('short', idx, 'question', e.target.value)}
                              className="w-full border border-blue-400 p-1 text-sm rounded mt-1"
                            />
                          ) : (
                            <span>{q.question}</span>
                          )}
                        </div>
                        <div className="whitespace-nowrap ml-2 rtl:mr-2">
                          {isEditing ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs font-normal text-slate-500">Marks:</span>
                              <input
                                type="number"
                                value={q.marks || 4}
                                onChange={(e) => updateQuestionItem('short', idx, 'marks', Number(e.target.value))}
                                className="w-12 border border-emerald-500 px-1 py-0.5 text-xs text-center font-bold"
                              />
                            </div>
                          ) : (
                            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                              ({q.marks || 4} Marks)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION C: LONG QUESTIONS */}
              {((generatedTest.sections?.long && generatedTest.sections.long.length > 0) || (generatedTest.long && generatedTest.long.length > 0)) && (
                <div className="space-y-4 pt-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <h4 className="font-extrabold text-base uppercase text-slate-900">
                      {isUrdu ? 'حصہ سوم: تفصیلی سوالات' : 'Section C: Long / Essay Questions'}
                    </h4>
                    <span className="text-xs font-bold bg-slate-200 px-2.5 py-1 rounded">
                      [{ (generatedTest.sections?.long || generatedTest.long || []).reduce((acc, curr) => acc + (Number(curr.marks) || 10), 0) } Marks]
                    </span>
                  </div>

                  <div className="space-y-4 text-sm">
                    {(generatedTest.sections?.long || generatedTest.long || []).map((q, idx) => (
                      <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 shadow-sm space-y-2 my-4">
                        <div className="flex justify-between items-start font-bold text-slate-900">
                          <div className="flex-1">
                            <span>Q{idx + 1}. </span>
                            {isEditing ? (
                              <input
                                type="text"
                                value={q.question}
                                onChange={(e) => updateQuestionItem('long', idx, 'question', e.target.value)}
                                className="w-full border border-blue-400 p-1 text-sm rounded mt-1"
                              />
                            ) : (
                              <span>{q.question}</span>
                            )}
                          </div>
                          <div className="whitespace-nowrap ml-2 rtl:mr-2">
                            {isEditing ? (
                              <div className="flex items-center space-x-1">
                                <span className="text-xs font-normal text-slate-500">Marks:</span>
                                <input
                                  type="number"
                                  value={q.marks || 10}
                                  onChange={(e) => updateQuestionItem('long', idx, 'marks', Number(e.target.value))}
                                  className="w-12 border border-emerald-500 px-1 py-0.5 text-xs text-center font-bold"
                                />
                              </div>
                            ) : (
                              <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                                ({q.marks || 10} Marks)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Collapsible Answer Key & Rubrics Section */}
              <div className="pt-6 border-t-2 border-slate-200 space-y-4">
                <div className="flex items-center justify-between no-print">
                  <button
                    onClick={() => setShowAnswerKey(!showAnswerKey)}
                    className="bg-slate-900 text-slate-200 hover:text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center space-x-2 rtl:space-x-reverse"
                  >
                    <span>🔑</span>
                    <span>{showAnswerKey ? (isUrdu ? 'جوابات کی کلید چھپائیں' : 'Hide Answer Key & Rubric') : (isUrdu ? 'جوابات کی کلید دیکھیں' : 'Show Answer Key & Rubric')}</span>
                  </button>

                  <button
                    onClick={handleDownloadAnswerKeyPDF}
                    className="bg-purple-700 hover:bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition flex items-center space-x-1.5 rtl:space-x-reverse"
                  >
                    <span>📥</span>
                    <span>{isUrdu ? 'جوابات کی کلید PDF' : 'Download Answer Key PDF'}</span>
                  </button>
                </div>

                {showAnswerKey && (
                  <div className="p-5 bg-slate-50 border border-slate-300 rounded-xl space-y-4 text-xs">
                    <h5 className="font-bold text-base text-slate-900 border-b pb-2 flex items-center space-x-2 rtl:space-x-reverse">
                      <span>🔑</span>
                      <span>{isUrdu ? 'جوابات کی مکمل کلید اور نمبروں کی تقسیم (Answer Key & Rubrics)' : 'Complete Answer Key & Marking Rubrics'}</span>
                    </h5>
                    
                    {/* MCQ Solutions */}
                    {generatedTest.answer_key?.mcqs?.length > 0 && (
                      <div className="space-y-2">
                        <div className="font-bold text-slate-800 text-xs uppercase tracking-wide">Section A (MCQs Solutions):</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {generatedTest.answer_key.mcqs.map((ak, idx) => (
                            <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 text-slate-800 space-y-0.5">
                              <div className="font-bold text-blue-700">MCQ Q{ak.q || idx+1}: Answer [{String(ak.answer || 'A').toUpperCase()}]</div>
                              {ak.text && <div className="text-slate-700 font-medium">{ak.text}</div>}
                              {ak.reason && <div className="text-[11px] text-slate-500 italic">Reason: {ak.reason}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Short Questions Rubric */}
                    {generatedTest.answer_key?.short?.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <div className="font-bold text-slate-800 text-xs uppercase tracking-wide">Section B (Short Questions Rubric):</div>
                        <div className="space-y-1.5">
                          {generatedTest.answer_key.short.map((ak, idx) => (
                            <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 text-slate-800 flex justify-between items-start">
                              <div>
                                <span className="font-bold text-indigo-700">Q{ak.q || idx+1}: </span>
                                <span className="text-slate-700">{ak.rubric || 'Full credit for accurate definition and diagram/example.'}</span>
                              </div>
                              <span className="text-[10px] font-bold bg-slate-100 px-2 py-0.5 rounded whitespace-nowrap ml-2">({ak.marks || 4} Marks)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Long Questions Rubric */}
                    {generatedTest.answer_key?.long?.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <div className="font-bold text-slate-800 text-xs uppercase tracking-wide">Section C (Long Questions Marking Breakdown):</div>
                        <div className="space-y-1.5">
                          {generatedTest.answer_key.long.map((ak, idx) => (
                            <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 text-slate-800 flex justify-between items-start">
                              <div>
                                <span className="font-bold text-indigo-700">Q{ak.q || idx+1}: </span>
                                <span className="text-slate-700">{ak.rubric || 'Detailed explanation (4m) + Application (3m) + Examples (3m)'}</span>
                              </div>
                              <span className="text-[10px] font-bold bg-slate-100 px-2 py-0.5 rounded whitespace-nowrap ml-2">({ak.marks || 10} Marks)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>

            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default AITestGenerator;
